/**
 * Who holds which seat in the grid.
 *
 * In a room bigger than one page you hear a voice with no face anywhere on
 * screen, so the speaker has to be brought forward. The question is what it
 * costs, and the obvious answer is the expensive one: sorting the roster so the
 * speaker lands second pushes every tile behind them back a place, so fifteen
 * people slide every time the floor changes. Jitsi ships that and its users
 * filed it as a bug — "people are constantly moving around the screen and it
 * makes it difficult to carry on a conversation" (jitsi-meet#10714).
 *
 * So this is a SEAT ALLOCATOR, not a sort. The grid is a fixed set of seats;
 * a speaker you cannot see is SWAPPED with one page-one occupant and nobody
 * else moves at all. Two tiles change instead of fifteen. It is what Teams
 * does, and what LiveKit's `useVisualStableUpdate` exists to do.
 *
 * Which occupant loses their seat is least-recently-spoken. Someone who has
 * just held the floor keeps their seat while a speaker who has been quiet
 * longest gives theirs up, so the page converges on the people actually in the
 * conversation rather than whoever happened to join first.
 *
 * The displaced person takes the newcomer's old seat — a true swap. That is
 * the part worth knowing when reading the grid: they do not go to the front of
 * page two, they go to wherever the speaker came from, which may be pages away.
 *
 * ## Why this carries state
 *
 * Seating persists across renders, and it has to. Deriving the order fresh from
 * the roster each time can only ever promote the CURRENT speaker, so the
 * previous one snaps back to page four the moment somebody else talks. Teams'
 * behaviour — 1, 2, 3, 15 then 1, 2, 30, 15 — is only reachable if the grid
 * remembers that seat four already belongs to 15.
 *
 * Every function here is pure: the caller holds the `Seating` and passes it
 * back in. Applying the same speaker twice is a no-op (they are already on page
 * one the second time), so a double-invoked render cannot double-swap.
 */

/**
 * The slice of a participant this needs, declared structurally so tests can
 * hand it plain objects instead of building whole Participants — the same
 * reason producer-pause-sync declares its own `PausableProducer`.
 */
export type SeatableTile = {
  id: string
  isLocal?: boolean
  /** 'additional' marks an extra camera, which never speaks. */
  sourceKind?: string
  /** Owner of an extra camera. */
  ownerId?: string
}

/**
 * Who sits where, and who spoke when.
 *
 * Treat as immutable — every function returns a new one rather than mutating,
 * so a stale copy cannot silently describe the current grid.
 */
export type Seating = {
  /** Participant ids, in seat order. Seat 0 is the first tile on page one. */
  readonly order: readonly string[]
  /**
   * The tick at which each id last took the floor. Absent means never, which
   * ranks as the most evictable — those seats are given up first.
   *
   * Deliberately NOT pruned to the current roster. A peer missing from the
   * roster has not necessarily left: with hide-tiles-without-video on, anyone
   * who turns their camera off drops out and comes back. Pruning would erase
   * the turn they just took, so muting a camera would cost them their standing
   * and, on return, their seat. The cost of keeping it is a few dozen bytes per
   * person who has ever spoken, which is nothing against a call's lifetime.
   */
  readonly spokeAt: ReadonlyMap<string, number>
  /** Monotonic counter. Only the ORDER of these values is meaningful. */
  readonly tick: number
  /**
   * The speaker already written into `spokeAt`.
   *
   * Without it, every recompute of an unchanged speaker would stamp a fresh
   * tick, so the person currently talking would keep resetting to
   * most-recent — which is harmless — while everyone else drifted apart in a
   * counter that climbs on render count rather than on turns taken.
   */
  readonly lastRecorded: string | null
}

export const emptySeating = (): Seating => ({
  order: [],
  spokeAt: new Map(),
  tick: 0,
  lastRecorded: null,
})

/**
 * The first-page size of a strip that scrolls instead of paging — a phone's
 * row of tiles under presented content (MobileStageLayout): the whole tiles
 * that fit in `span` px, each `tile` px with `gap` px between neighbours.
 *
 * A part-visible tile does not count. A face cut in half at the edge is not
 * "you can already see them", so a speaker in that seat is brought forward.
 */
export function tilesThatFit(span: number, tile: number, gap: number): number {
  if (span <= 0 || tile <= 0) return 0
  return Math.max(0, Math.floor((span + gap) / (tile + gap)))
}

/** Tiles that belong at the head: your own, and your own extra cameras. */
const isHead = (t: SeatableTile, localId: string | null | undefined) =>
  Boolean(t.isLocal) || (t.sourceKind === 'additional' && t.ownerId === localId)

/**
 * Seat the current roster, swapping an unseen speaker onto the first page.
 *
 * Returns the input `tiles` array unchanged (same reference) whenever the
 * seating already matches it, so callers memoising on identity do not re-render
 * to say nothing — which, at one recompute per speaker tick, is most of the
 * time in a normal conversation.
 *
 * @param prev            seating from the last call; `emptySeating()` to start
 * @param tiles           the roster, your own tile and extras already at the head
 * @param activeSpeakerId whoever is talking, or null
 * @param localId         your own participant id, for grouping your extras
 * @param firstPageSize   tiles on page one — the line between "you can already
 *                        see them" and "you cannot"
 */
export function seatActiveSpeaker<T extends SeatableTile>(
  prev: Seating,
  tiles: readonly T[],
  activeSpeakerId: string | null | undefined,
  localId: string | null | undefined,
  firstPageSize: number,
): { seating: Seating; tiles: readonly T[] } {
  // Tiles grouped by id rather than indexed by it. Ids are unique in practice —
  // VideoGrid keys on them — but an extra camera that carries its owner's id
  // would otherwise silently drop one of the two. Grouping seats them together
  // instead, which is also where an extra camera belongs.
  const groups = new Map<string, T[]>()
  for (const t of tiles) {
    const group = groups.get(t.id)
    if (group) group.push(t)
    else groups.set(t.id, [t])
  }

  // The tile that can speak for an id: a person, never one of their extra
  // cameras. Matching on id alone would let a second camera stand in for its
  // owner, promoting the camera and leaving the face behind.
  const primary = new Map<string, T>()
  for (const t of tiles) {
    if (t.sourceKind !== 'additional' && !primary.has(t.id)) primary.set(t.id, t)
  }

  // ── Reconcile the remembered order with who is actually here ──────────────
  // Keep everyone who is still in the room exactly where they were sitting;
  // append whoever has joined since. A leaver's seat closes up rather than
  // being held open, which is the one place the grid does shift — and it has
  // to, or a room that churns would fill page one with holes.
  const seen = new Set<string>()
  const head: string[] = []
  const body: string[] = []
  const place = (id: string) => {
    if (seen.has(id)) return
    seen.add(id)
    const tile = groups.get(id)?.[0]
    if (!tile) return
    // Your own tile and your own extras are pinned to the front. Asserted on
    // every pass rather than assumed from the input, so seating that predates
    // your own tile arriving still puts you first once it does.
    ;(isHead(tile, localId) ? head : body).push(id)
  }
  for (const id of prev.order) place(id)
  for (const t of tiles) place(t.id)

  const order = [...head, ...body]
  const headLen = head.length

  // ── Record that this person held the floor ────────────────────────────────
  // Done whether or not they are already visible: staying on page one is
  // exactly what a recent turn should buy, and the trace this reproduces
  // (1,2,3,15 -> 1,2,30,15) only works if seat four is protected afterwards.
  let spokeAt = prev.spokeAt
  let tick = prev.tick
  // Nothing to do about a `lastRecorded` who has since vanished from the grid.
  // Clearing it looks prudent — the same person could return still holding the
  // floor — but it cannot change an outcome: `lastRecorded` is only ever set to
  // the id that was recorded MOST recently, so that id already carries the
  // highest tick. Re-stamping it moves nobody. Mutation testing is what proved
  // the guard dead; it was written here first and removed again.
  let lastRecorded = prev.lastRecorded
  const speaker = activeSpeakerId ? primary.get(activeSpeakerId) : undefined
  if (speaker && !speaker.isLocal && activeSpeakerId !== lastRecorded) {
    const next = new Map(spokeAt)
    next.set(speaker.id, tick)
    spokeAt = next
    tick += 1
    lastRecorded = speaker.id
  }

  // ── Swap them in, if they are not already on screen ───────────────────────
  const speakerSeat = speaker ? order.indexOf(speaker.id) : -1
  // `speakerSeat < firstPageSize` covers "already visible", which is the common
  // case and deliberately does nothing: you are looking at them either way, and
  // moving them would cost a reshuffle to tell you something the screen is
  // already saying.
  if (speakerSeat >= firstPageSize) {
    const victim = chooseVictim(order, primary, headLen, firstPageSize, spokeAt)
    if (victim >= 0) {
      const swapped = [...order]
      swapped[victim] = order[speakerSeat]
      swapped[speakerSeat] = order[victim]
      order.length = 0
      order.push(...swapped)
    }
  }

  // ── Rebuild the tile list in seat order ───────────────────────────────────
  const next: T[] = []
  for (const id of order) {
    const group = groups.get(id)
    if (group) next.push(...group)
  }

  const seating: Seating = { order, spokeAt, tick, lastRecorded }
  const unchanged = next.length === tiles.length && next.every((t, i) => t === tiles[i])
  return { seating, tiles: unchanged ? tiles : next }
}

/**
 * Which page-one seat the newcomer takes.
 *
 * Least-recently-spoken, with never-spoken counting as longest-ago. Among
 * several who have never spoken the one furthest back gives way first, so a
 * quiet page empties from the end rather than from the seat beside you —
 * which is what makes the first few promotions land as 4, then 3, then 2.
 *
 * Your own tile and your own extras are out of reach by construction — they
 * live in the head, and the scan starts past it. Nobody's extra camera is a
 * candidate either: kicking one out separates it from its owner and is usually
 * the opposite of what it was started for. Returns
 * -1 when page one holds nothing that may be moved, in which case the speaker
 * stays where they are rather than something arbitrary being displaced.
 */
function chooseVictim<T extends SeatableTile>(
  order: readonly string[],
  primary: ReadonlyMap<string, T>,
  headLen: number,
  firstPageSize: number,
  spokeAt: ReadonlyMap<string, number>,
): number {
  let victim = -1
  let victimNeverSpoke = false

  for (let seat = headLen; seat < Math.min(firstPageSize, order.length); seat++) {
    const tile = primary.get(order[seat])
    // No primary tile means this seat holds only an extra camera, which is
    // never given up: taking it would strand the camera away from its owner.
    //
    // Your own tile needs no test here. Every local tile is forced into the
    // head by `isHead`, and this scan starts after the head — so a local tile
    // cannot reach this line. Mutation testing is what showed the `isLocal`
    // check that used to sit here could be deleted without failing anything.
    if (!tile) continue

    const at = spokeAt.get(order[seat])
    if (at === undefined) {
      // Never spoken. Overwriting on each hit walks forward to the LAST such
      // seat, which is the one to give up first.
      victim = seat
      victimNeverSpoke = true
      continue
    }
    // Someone who has spoken can only win while no silent seat has been found.
    if (victimNeverSpoke) continue
    if (victim < 0 || at < spokeAt.get(order[victim])!) victim = seat
  }

  return victim
}
